import { TypeChecker } from '@attain-apps/type-guards'

// TODO: evaluate using this: https://www.npmjs.com/package/lz-string (see
// https://garrett-bodley.medium.com/encoding-data-inside-of-a-url-query-string-f286b7e20465)

/**
 * Represents an arbitrary value stored as a base64-encoded query param
 *
 * Can be used to store complex objects of data in a relatively compact query
 * param
 */
export interface EncodedQueryParam<TValue = unknown> {
  /** @see {@link Scope}*/
  scope: EncodedQueryParam.Scope
  /**
   * Represents a value stored in the query parameters under the specified {@link scope}.
   *
   * __NOTE:__ Complex objects are stored as JSON strings. The value stored in the
   * query parameters is the result of {@link JSON.stringify} applied to the
   * object.
   */
  value: TValue
}
export namespace EncodedQueryParam {
  /**
   * The key of the query param under which all {@link EncodedQueryParam} are
   * stored
   *
   * __NOTE:__ this cannot be `state` as that collides with a query param used
   * by Cognito
   */
  export const KEY = 'o' // short for 'other,' and unlikely to conflict with anything else

  /**
   * A series of strings representing the nesting levels of an object.
   *
   * @example
   * const scope: Scope = ['a', 'b', 'c']
   * // produces
   * const produced =  {
   *    a: {
   *      b: {
   *        c: 'some value'
   *      }
   *    }
   *  }
   */
  export type Scope = readonly string[]

  /**
   * Applies the {@link EncodedQueryParam} to the target object.
   *
   * @example
   * const target = {
   *  a: {
   *   b: {
   *    name: 'John',
   *    age: 25,
   *   }
   *  }
   * }
   * const param: QueryParam = {
   *  scope: ['a', 'b', 'c'],
   *  value: { name: 'Jane' },
   * }
   *
   * const result = QueryParam.apply(target, param)
   * // produces
   * const produced =  {
   *   a: {
   *    b: {
   *     c: { name: 'Jane' },
   *     name: 'John',
   *     age: 25,
   *    }
   *  }
   * }
   */
  export function apply(
    encodedQueryParams: Readonly<FullObject>,
    { value, scope }: Readonly<EncodedQueryParam>,
  ): FullObject {
    if (scope.length === 0) {
      throw new Error('Scope must have length of at least one')
    }
    encodedQueryParams = { ...encodedQueryParams }
    let obj: FullObject = encodedQueryParams
    for (const key of scope.slice(0, scope.length - 1)) {
      obj = obj[key] =
        !TypeChecker.isObject(obj[key]) || Array.isArray(obj[key])
          ? {}
          : { ...obj[key] }
    }

    obj[scope[scope.length - 1]] = value

    return encodedQueryParams
  }

  /**
   * Contains the values of all {@link EncodedQueryParam}
   */
  export type FullObject = Record<string, any>
  export namespace FullObject {
    /**
     * Encodes the query params to a base64 string
     *
     * @see {@link encodeURIComponent}, {@link btoa}, {@link JSON.stringify}
     */
    export function encode(obj: Readonly<FullObject>): string | undefined {
      const rawJson = JSON.stringify(obj)
      return rawJson === '{}' ? undefined : encodeURIComponent(btoa(rawJson))
    }

    /**
     * Decodes the query params from a base64 string
     *
     * @see {@link decodeURIComponent}, {@link atob}, {@link JSON.parse}
     */
    export function decode(encoded: string | undefined): FullObject {
      return encoded
        ? (JSON.parse(atob(decodeURIComponent(encoded))) as object)
        : {}
    }
  }
}
